/** @file clip.mirrorPose: writes the left/right mirror of the pose at a frame (to the same or another frame). */
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import type { CommandDef } from '../apply/commandDef';
import { mirrorPose } from '../pose/mirrorPose';
import { samplePose } from '../pose/samplePose';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { keyedNodes } from './keyedNodes';
import { writePose } from './writePose';

/**
 * `clip.mirrorPose { clip, frame, to?, nodes? }` → `{ frame }`: the sampled pose mirrored (twins swapped) at
 * `to ?? frame`, replacing that key's nodes (events kept) or merging when `nodes` is given; warns `NO_MIRROR_TWIN`.
 */
export const clipMirrorPoseCommand: CommandDef<'clip.mirrorPose'> = {
  name: 'clip.mirrorPose',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'frame', 'to', 'nodes']);
    return compact({
      clip: r.str('clip'), frame: r.int('frame', { min: 0 }), to: r.opt('to', (k) => r.int(k, { min: 0 })),
      nodes: r.opt('nodes', (k) => r.check(k, checkArray(checkString())) as string[]),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    const { pose, warnings } = mirrorPose(draft.puppet, samplePose(clip, input.frame, input.nodes ?? keyedNodes(clip)));
    const out = writePose(draft, { clipName: name, frame: input.to ?? input.frame, nodes: pose, merge: input.nodes !== undefined });
    return { ...out, warnings: [...warnings, ...out.warnings] };
  },
};
